import React from 'react';
import dayjs from 'dayjs';

import ActivityListItem from '../activitylist/ActivityListItem';
import AttachmentFavorite from '../attachmentlink/AttachmentFavorite';
import AttachmentIcon from '../attachmentlink/AttachmentIcon';
import AttachmentLink from '../attachmentlink/AttachmentLink';
import Modal from '../modal/Modal';
import Icon from '../icon/Icon';
import LinkButton from '../../forms/linkButton/LinkButton';

import { IEmailAttachment, IGroup, IGroupMessage, IGroupMember } from '../../../store/group/groupState';
import { deleteMessage } from '../../../services/messages';

export enum DisplayMode {
    Snippet,
    Full
}

interface IOwnProps {
    message: IGroupMessage;
    displayMode: DisplayMode;
    group: IGroup;
    groupMembers: IGroupMember[];
    index: number;
    isDisconnected?: boolean;
    isDi: boolean;
    onDeleted?: () => void;
}

interface IOwnState {
    showEventList: boolean;
    showConfirmDeleteMessage: boolean;
    isDeletingMessage: boolean;
}

type Props = IOwnProps;

class Message extends React.Component<Props, IOwnState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            showEventList: false,
            showConfirmDeleteMessage: false,
            isDeletingMessage: false
        }
    }

    public render() {
        return this.props.displayMode === DisplayMode.Snippet ? this.renderSnippet() : this.renderFull();
    }

    private renderSnippet = () => {
        return <ActivityListItem link={`/Group/${this.props.group.reference}/Message/${this.props.message.threadId}`} imageUrl={this.props.message.messageCreator && this.getUserImage} hasArrow={true} tags={this.renderTags} preheader={this.props.message.htmlBody} information={this.renderMessageEventInfo}>
            <p className="meta"><strong>{this.props.message.messageCreator ? this.props.message.messageCreator.personName : this.props.message.fromName || 'Ukendt afsender'}</strong>, {dayjs(this.props.message.created).format('D. MMM YYYY HH:mm')}</p>
            <h2 className="activity-list__item__body__title">{this.props.message.subject}</h2>
        </ActivityListItem>;
    };

    private renderFull = () => {
        return <ActivityListItem imageUrl={this.props.message.messageCreator && this.getUserImage} tags={this.renderTags} article={this.props.message.htmlBody} isPadded={this.props.index > 0} information={this.renderMessageEventInfo}>
            <p className="meta"><strong>{this.props.message.messageCreator ? this.props.message.messageCreator.personName : this.props.message.fromName ||'Ukendt afsender'}</strong>, {dayjs(this.props.message.created).format('D. MMM YYYY HH:mm')}</p>
            {this.props.index === 0 && <h2 className="activity-list__item__body__title">{this.props.message.subject}</h2>}
        </ActivityListItem>;
    };

    private get getUserImage() {
        if (this.props.message.messageCreator && this.props.message.messageCreator.companyId === 1) {
            return this.props.message.messageCreator.personImageUrl;
        }

        const groupMember = this.props.groupMembers.find(m => !!this.props.message.messageCreator && m.diNumber === this.props.message.messageCreator.personId);

        return groupMember ? groupMember.imageUrl : null;
    }

    private get renderTags() {
        return this.props.message.attachments && this.props.message.attachments.length > 0 ?
            <React.Fragment>
                {this.props.message.attachments.map(this.renderAttachment)}
            </React.Fragment>
            :
            null;
    }

    private renderAttachment = (attachment: IEmailAttachment) => {
        return <li key={attachment.id} className="tags__list__item">
            <AttachmentLink className="button button--tag" document={attachment} title={attachment.fileName}>
                <AttachmentFavorite id={attachment.id} groupId={this.props.message.groupId} messageId={this.props.message.messageId} isFavorite={attachment.isFavorite} isDisconnected={this.props.isDisconnected} />
                <AttachmentIcon mimeType={attachment.mimeType} />
                {attachment.fileName.length > 20 ? <React.Fragment>{attachment.fileName.substring(0, 18)}&hellip;</React.Fragment> : attachment.fileName}
            </AttachmentLink>
        </li>;
    }

    private get renderMessageEventInfo() {
        if (!this.props.isDi || this.props.message.messageEvents == null) return null;

        const members = this.props.groupMembers.filter(m => m.role !== 'Responsible' && m.role !== 'Coordinator');
        const hasRead = this.props.message.messageEvents.filter(x => x.event === 'open' && members.find(m => m.diNumber === x.personId) !== undefined);

        return <React.Fragment>
            {(hasRead && hasRead.length > 0) &&
                <React.Fragment>
                    Set af {hasRead.length}.
                    {' '}
                    <span className="link" style={{ cursor: "pointer" }} onClick={this.messageEventsClicked}>Se liste</span>.
                    {' \u00A0 \u00A0 \u00A0 \u00A0 '}

                    <Modal show={this.state.showEventList} onClose={this.messageEventsClicked}>
                        <div className="rich-text">
                            <p>
                                <h3>Følgende har set:</h3>
                                {hasRead.map(read => {
                                    return <div key={read.personId}>
                                        {members.find(m => m.diNumber === read.personId)!.fullName}
                                    </div>
                                })}
                            </p>
                            {members.length <= 50 &&
                                <p>
                                    <h3>Følgende har ikke set:</h3>
                                    {members.filter(m => hasRead.find(hr => hr.personId === m.diNumber) === undefined).map(unread => {
                                        return <div key={unread.diNumber}>
                                            {unread.fullName}
                                        </div>
                                    })}
                                </p>
                            }
                        </div>
                    </Modal>
                </React.Fragment>
            }

            <React.Fragment>
                <a href="/#" className="button button--action has-icon" onClick={this.toggleConfirmDeleteMessage}>
                    <span className="text">
                        <span className="icon icon--right-spacing">
                            <Icon name="trash" />
                        </span>
                        Slet besked
                    </span>
                </a>

                <Modal show={this.state.showConfirmDeleteMessage} onClose={this.toggleConfirmDeleteMessage}>
                    <div className="rich-text">
                        <p>
                            <h3>Slet besked?</h3>
                            Du har valgt at slette beskeden "{this.props.message.subject}". Hvis du sletter beskeden vil den blive fjernet fra gruppen sammen med eventuelle svar til beskeden. Vil du fortsætte?
                        </p>
                    </div>

                    <div className="modal__content__form__actions modal__content__form__actions--border">
                        <div className="flex-grid">
                            <div className="flex-grid__col flex-grid__col--12 flex-grid__col--sm-12 flex-grid__col--lg-12 align-right form__section__inner">
                                <LinkButton modifierClass="button--discreet button--light" disabled={this.state.isDeletingMessage} onClick={this.toggleConfirmDeleteMessage}>Annuller</LinkButton>
                                <LinkButton modifierClass="button--primary button--primary button--large" spinning={this.state.isDeletingMessage} onClick={this.saveDocumentMessage}>Slet</LinkButton>
                            </div>
                        </div>
                    </div>
                </Modal>
            </React.Fragment>
        </React.Fragment>
    };

    private messageEventsClicked = (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        this.setState(prevState => ({
            showEventList: !prevState.showEventList
        }));
    }

    private toggleConfirmDeleteMessage = (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        this.setState(prevState => ({
            showConfirmDeleteMessage: !prevState.showConfirmDeleteMessage
        }));
    }

    private saveDocumentMessage = async (e: React.FormEvent<HTMLAnchorElement> | React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        if (!this.state.isDeletingMessage) {
            this.setState({
                isDeletingMessage: true
            });

            await deleteMessage(this.props.group!.id, this.props.message.messageId);

            if (this.props.onDeleted) {
                await this.props.onDeleted();
            }

            this.setState({
                isDeletingMessage: false,
                showConfirmDeleteMessage: false
            });
        }
    }
}

export default Message;